import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

export interface MovedSettingsNoticeProps {
  /** Which channel page is showing the notice — only copy varies. */
  channel: 'telegram' | 'max';
  /** `settings.chatbot.view` — the Chatbot behaviour page (shipped, plan-merchant-portal-019 WP2). */
  canViewChatbot: boolean;
  /** `content.view` — the Bot texts & FAQ editor (shipped this release). */
  canViewTemplates: boolean;
  /** `settings.channels.view` — the touchpoint on/off registry (shipped this release). */
  canViewChannels: boolean;
  /**
   * `settings.tenant.view` — Company, the storefront-profile destination. Telegram only: the MAX
   * page never carried a storefront-profile section to lose.
   */
  canViewTenant?: boolean;
}

/**
 * "Where did it go" — persistent orientation for merchants who have used the Telegram/MAX page
 * for a long time and will look there first for behaviour and texts that moved elsewhere
 * (design-channel-connection-pages.md §6). Placed above the read-only alert, not dismissible.
 * A target whose view code the viewer does not hold renders as plain text, never a dead link.
 *
 * The interim "Chatbot behaviour (general)" bridge card that used to live below this notice on
 * `TelegramSettingsPage` (plan-conversation-001 WP-MP.2's sequencing fix) is retired now that the
 * real `/settings/chatbot` page has shipped (plan-merchant-portal-019 WP2) — this notice always
 * points at that page instead of naming a temporary card or a "not shipped yet" sentence.
 */
export function MovedSettingsNotice({
  channel,
  canViewChatbot,
  canViewTemplates,
  canViewChannels,
  canViewTenant = false,
}: MovedSettingsNoticeProps) {
  const { t } = useTranslation('settings');
  const channelLabel = t(`movedSettingsNotice.channel.${channel}`);

  return (
    <Alert severity="info" icon={false}>
      <AlertTitle component="h2">{t('movedSettingsNotice.title')}</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {t('movedSettingsNotice.body', { channel: channelLabel })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('movedSettingsNotice.chatbotShippedSentence')}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {canViewChatbot ? (
          <Button size="small" variant="outlined" component={RouterLink} to="/settings/chatbot">
            {t('movedSettingsNotice.chatbotLink')}
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {t('movedSettingsNotice.chatbotLink')}
          </Typography>
        )}
        {canViewTemplates ? (
          <Button
            size="small"
            variant="outlined"
            component={RouterLink}
            to="/settings/chatbot/templates"
          >
            {t('movedSettingsNotice.templatesLink')}
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {t('movedSettingsNotice.templatesLink')}
          </Typography>
        )}
        {canViewChannels ? (
          <Button size="small" variant="outlined" component={RouterLink} to="/settings/channels">
            {t('movedSettingsNotice.channelsLink')}
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {t('movedSettingsNotice.channelsLink')}
          </Typography>
        )}
        {channel === 'telegram' ? (
          canViewTenant ? (
            <Button size="small" variant="outlined" component={RouterLink} to="/settings/tenant">
              {t('movedSettingsNotice.companyLink')}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {t('movedSettingsNotice.companyLink')}
            </Typography>
          )
        ) : null}
      </Stack>
    </Alert>
  );
}
